import { useEffect } from "react";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import FeaturedCourses from "@/components/site/FeaturedCourses";
import CinematicBreak from "@/components/site/CinematicBreak";
import AudienceSplit from "@/components/site/AudienceSplit";
import { ServicesTeaser } from "@/components/site/ServicesTeaser";
import Courses from "@/components/site/Courses";
import CourseQuiz from "@/components/site/CourseQuiz";
import Process from "@/components/site/Process";
import Team from "@/components/site/Team";
import EditorLetter from "@/components/site/EditorLetter";
import PartnerBanner from "@/components/site/PartnerBanner";
import ChapterDivider from "@/components/site/ChapterDivider";
import Testimonials from "@/components/site/Testimonials";
import HeldenCampus from "@/components/site/HeldenCampus";
import PflichtfortbildungenSection from "@/components/site/PflichtfortbildungenSection";
import { Brochure } from "@/components/site/PolishSections";
import BundleBooster from "@/components/site/BundleBooster";
import AtlasPromo from "@/components/site/AtlasPromo";
import EditorialBookshelf from "@/components/site/EditorialBookshelf";
import KalenderPromo from "@/components/site/KalenderPromo";
import Faq, { faqItems } from "@/components/site/Faq";
import LeadForm from "@/components/site/LeadForm";
import Footer from "@/components/site/Footer";
import StickyCta from "@/components/site/StickyCta";
import SeoHead from "@/components/site/SeoHead";
import { COURSE_LIST } from "@/data/courses";

export default function Landing() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
    document.title = "Dickies-Campus – AZAV Weiterbildung Pflege mit Bildungsgutschein";
    const desc = document.querySelector('meta[name="description"]');
    const content =
      "Online-Weiterbildungen Pflege mit Bildungsgutschein – AZAV zertifiziert, Start jeden Montag. LG1/LG2, PDL, Praxisanleitung & mehr. Jetzt kostenlose Beratung sichern.";
    if (desc) desc.setAttribute("content", content);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = content;
      document.head.appendChild(m);
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <SeoHead courses={COURSE_LIST} faqs={faqItems} />
      <Header />
      <main>
        {/* 1 · Hero – die Fotografie trägt die Seite */}
        <Hero />

        {/* 1a · Kalender-Edition 2027 · Teaser „Bald bei Amazon" mit Blätterbuch */}
        <section className="bg-cream-50 border-b border-cream-300 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <KalenderPromo testId="home-kalender-promo" />
          </div>
        </section>

        {/* 1b · Beliebteste Weiterbildungen – Top-5-Kurse direkt sichtbar (Conversion-Booster) */}
        <FeaturedCourses />

        {/* 1c · Cinematic Editorial-Break — Reel-Look · Externenprüfung */}
        <CinematicBreak />

        {/* 2 · Zwei Wege. Ein Ziel. – B2B / B2C Split */}
        <AudienceSplit />

        {/* 2b · Services für Pflegedienste – Joudi Ltd (Backoffice, Homepage, Ads) */}
        <ServicesTeaser />

        {/* Chapter Nº 01 · warm burgundy break — introduces the "how it works" chapter */}
        <ChapterDivider
          n="03"
          kicker="Kapitel Eins"
          title="Wie wir denken."
          quote="Weiterbildung darf sich nicht anfühlen wie eine zweite Ausbildung. Sie darf sich anfühlen wie ein Kaffee mit Menschen, die den Beruf lieben."
          attribute="— Julia, Dozentin"
          photo="/ads/home_team_walk.png"
          variant="burgundy"
          testId="chapter-01"
        />

        {/* 2c · Quiz – welche Weiterbildung passt zu mir? */}
        <CourseQuiz />

        {/* 3 · Kurs-Katalog */}
        <Courses />

        {/* 3b · Partnerschule Mönchengladbach — Ausbildungsplätze zum 01.11. */}
        <PartnerBanner />

        {/* 4 · Wie es abläuft */}
        <Process />

        {/* Editor's Letter — signature intimate moment */}
        <EditorLetter />

        {/* 5 · Dozentinnen – echte Menschen */}
        <Team />

        {/* Chapter Nº 02 · peach break — introduces the "campus / community" chapter */}
        <ChapterDivider
          n="04"
          kicker="Kapitel Zwei"
          title="Wo du bleibst."
          quote="Der Kurs ist ein Anfang. Der Campus ist das Zuhause danach — die Pflege-KI, die Schatzkiste, die Nächte, in denen du kurz nicht mehr weiterweißt."
          attribute="— Annika, Dozentin"
          photo="/ads/home_care_hands.png"
          variant="peach"
          testId="chapter-02"
        />

        {/* 6 · Stimmen aus dem Kurs */}
        <Testimonials />

        {/* Chapter Nº 03 · Bordeaux — leitet zum Werkzeug/Campus-Teil über */}
        <ChapterDivider
          n="05"
          kicker="Kapitel Drei"
          title="Womit du arbeitest."
          quote="Die beste Weiterbildung nützt nichts, wenn der Alltag dich zurückzieht. Deshalb bekommst du bei uns Werkzeuge, die die tägliche Doku übernehmen — damit du wieder Zeit für Menschen hast."
          attribute="— Studio DH"
          photo="/images/brand/behandlungspflege.jpg"
          layout="split"
          variant="burgundy"
          testId="chapter-03"
        />

        {/* 7 · Pflege-KI Funnel – Story + 3 Schritte + 2 Pakete (199/349) in einer Sektion */}
        <HeldenCampus />

        {/* 9b · Pflichtfortbildungen – B2B-Sektion mit allen Themen + Rezertifizierungen */}
        <PflichtfortbildungenSection />

        {/* 9c · Editorial Bookshelf · Alle 12 Magazine im redaktionellen Regal */}
        <EditorialBookshelf />

        {/* 10 · Broschüre Lead-Magnet */}
        <Brochure />

        {/* 9 · FAQ */}
        <Faq />

        {/* 9b · Bundle-Booster · Last-Chance Conversion vor Kontakt */}
        <BundleBooster />

        {/* 9c · Atlas-Hook · 253-Seiten-Premium-PDF als Trust-Anker (auch ohne Bundle-Interesse) */}
        <section className="bg-cream-50 border-t border-cream-300 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AtlasPromo variant="b2b" testId="home-atlas-promo" />
          </div>
        </section>

        {/* 10 · Kontakt */}
        <LeadForm />
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}
